import { useFeatureFlags } from "@/core/auth/hooks";
import { useDeviceFleet } from "@/modules/device/hooks/useDeviceFleet";
import { Label } from "@/modules/shadcn/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import type { Device } from "@repo/schema";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const LOCAL_VALUE = "__local__";

export interface DeviceSelectFieldProps {
  value: number | null;
  onChange: (deviceId: number | null) => void;
}

/** MXIDs identify the physical camera far better than the generated device id. */
const describeDevice = (device: Device): string =>
  device.cameras.length > 0
    ? device.cameras.map((camera) => camera.mxid).join(", ")
    : device.deviceId;

const OnlineDot = ({ online }: { online: boolean }) => (
  <span
    aria-hidden
    className={
      online ? "size-2 rounded-full bg-emerald-500" : "size-2 rounded-full bg-black/20"
    }
  />
);

/**
 * Picks which device a project reaches its camera through.
 *
 * Choosing "local network" clears the device and hands the project back to the
 * address field, which is how a site with no internet keeps working — and is
 * offered only when the organization has local camera access enabled.
 */
export const DeviceSelectField = ({ value, onChange }: DeviceSelectFieldProps) => {
  const { t } = useTranslation(["device", "common"]);
  const { devices, isLoading, isError } = useDeviceFleet();
  const { localCameraAccess } = useFeatureFlags();

  const byId = useMemo(
    () => new Map(devices.map((device) => [String(device.id), device])),
    [devices],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="projectDevice" className="font-semibold">
        {t("select.label")}
      </Label>
      {/* With local access off there is no "local" option to sit on, so an
          unset project shows the placeholder rather than an option the
          organization cannot actually use. */}
      <Select
        value={
          value !== null ? String(value) : localCameraAccess ? LOCAL_VALUE : null
        }
        onValueChange={(next) =>
          onChange(next === LOCAL_VALUE || next === null ? null : Number(next))
        }
      >
        <SelectTrigger id="projectDevice" className="w-full flex-1">
          {isLoading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Spinner className="size-4" />
              {t("select.loading")}
            </span>
          ) : (
            // Base UI renders the raw value unless given a render function, so
            // without this the trigger shows the numeric device id.
            <SelectValue placeholder={t("select.placeholder")}>
              {(selected: string) => {
                if (selected === LOCAL_VALUE) {
                  return <span>{t("select.localNetwork")}</span>;
                }

                const device = byId.get(selected);

                if (!device) {
                  return (
                    <span className="text-muted-foreground">
                      {t("select.unavailable")}
                    </span>
                  );
                }

                return (
                  <span className="flex items-center gap-2">
                    <OnlineDot online={device.online} />
                    {describeDevice(device)}
                  </span>
                );
              }}
            </SelectValue>
          )}
        </SelectTrigger>
        <SelectContent>
          {localCameraAccess && (
            <SelectItem value={LOCAL_VALUE}>{t("select.localNetwork")}</SelectItem>
          )}
          {devices.map((device) => (
            <SelectItem key={device.id} value={String(device.id)}>
              <span className="flex items-center gap-2">
                <OnlineDot online={device.online} />
                {describeDevice(device)}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!isLoading && !isError && devices.length === 0 && (
        <p className="text-xs text-black/45">
          {localCameraAccess
            ? t("select.emptyLocal")
            : t("select.empty")}
        </p>
      )}
      {isError && (
        <p className="text-xs text-black/45">
          {localCameraAccess
            ? t("select.errorLocal")
            : t("select.error")}
        </p>
      )}
    </div>
  );
};
